import clsx from "clsx";
import { FC, MouseEvent, MouseEventHandler, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  formatTimeISO,
  IRichLogRecord,
  isTranslatableItem,
  MAIN_TIME_FORMAT_SEC,
  MAIN_TIME_FORMAT_SEC_MS,
  onActivateKeyDown,
  RichLogLevel,
  translateInfoItemDecoded,
  useTimezone,
} from "@ryde/shared";

import { useGenerateLogHtml } from "./hooks";
import { RichLogsIdStripRow } from "./rich-logs.id-strip-row";
import { extractPriceRateSearchIdStrips } from "./rich-logs.id-strips";
import { IRichLogsFilter, RichLogFormFields } from "./rich-logs.models";
import { highlightText } from "./rich-logs.utils";

interface IRichViewLogItem {
  record: IRichLogRecord;
  onItemClick: MouseEventHandler<HTMLDivElement>;
  searchText?: string;
  idOnlyLinks?: boolean;
}

export const RichViewLogItem: FC<IRichViewLogItem> = ({
  record: recordProp,
  onItemClick,
  searchText,
  idOnlyLinks = false,
}) => {
  const record = useMemo(() => ({ ...recordProp }), [recordProp]);
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { generateHtmlFromRichRecord } = useGenerateLogHtml({ idOnlyLinks });
  const { watch } = useFormContext<IRichLogsFilter>();

  const {
    timestamp,
    level,
    group,
    message,
    step,
    method,
    error,
    errorDetails,
    translatableItem,
    diagnostics,
    aiHint,
  } = record;

  const showDebugInfo = watch(RichLogFormFields.showDebugInfo) || false;
  const withAiHintsOnly = watch(RichLogFormFields.withAiHintsOnly) || false;
  const showAiHint = (showDebugInfo || withAiHintsOnly) && !!aiHint;

  const shouldShowTranslatedError = error && isTranslatableItem(error);
  const shouldShowTranslatedItem =
    translatableItem && isTranslatableItem(translatableItem);

  const shouldShowErrorDetails = errorDetails && showDebugInfo;
  const idStrips = useMemo(
    () => extractPriceRateSearchIdStrips(record),
    [record],
  );

  const messageHtml = useMemo(() => {
    if (!message) {
      return undefined;
    }

    let html = generateHtmlFromRichRecord(record);

    if (searchText) {
      // Split search text by commas for multi-value search
      const searchTerms = searchText
        .split(",")
        .map(term => term.trim())
        .filter(term => term.length);
      if (searchTerms.length) {
        html = highlightText(html, searchTerms);
      }
    }

    return html;
  }, [message, generateHtmlFromRichRecord, record, searchText]);

  const methodWithDiagnostics = useMemo(() => {
    if (!method || !showDebugInfo) {
      return undefined;
    }

    let methodText = method;

    if (diagnostics) {
      const { rssGb, heapTotalGb, heapUsedGb } = diagnostics;
      methodText = `${method} (rss: ${rssGb}gb, heapTotal: ${heapTotalGb}gb, heapUsed: ${heapUsedGb}gb)`;
    }

    if (searchText) {
      // Split search text by commas for multi-value search
      const searchTerms = searchText
        .split(",")
        .map(term => term.trim())
        .filter(term => term.length);
      if (searchTerms.length) {
        return highlightText(methodText, searchTerms);
      }
    }

    return methodText;
  }, [method, showDebugInfo, searchText, diagnostics]);

  const aiHintHtml = useMemo(() => {
    if (!showAiHint || !aiHint) {
      return undefined;
    }
    const text = aiHint;
    if (searchText) {
      const searchTerms = searchText
        .split(",")
        .map(term => term.trim())
        .filter(term => term.length);
      if (searchTerms.length) {
        return highlightText(text, searchTerms);
      }
    }
    return text;
  }, [showAiHint, aiHint, searchText]);

  const errorDetailsHtml = useMemo(() => {
    if (!errorDetails || !showDebugInfo) {
      return undefined;
    }

    if (searchText) {
      // Split search text by commas for multi-value search
      const searchTerms = searchText
        .split(",")
        .map(term => term.trim())
        .filter(term => term.length > 0);
      if (searchTerms.length > 0) {
        return highlightText(errorDetails, searchTerms);
      }
    }

    return errorDetails;
  }, [errorDetails, showDebugInfo, searchText]);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onItemClick}
      onKeyDown={onActivateKeyDown(event =>
        onItemClick(event as unknown as MouseEvent<HTMLDivElement>),
      )}
      className={clsx(
        "log__item",
        level && `log__item--level_${level.toLowerCase()}`,
      )}
    >
      <span className="log__item--time">
        {formatTimeISO({
          dateTimeISO: timestamp,
          timeFormat: showDebugInfo
            ? MAIN_TIME_FORMAT_SEC_MS
            : MAIN_TIME_FORMAT_SEC,
          timezone,
        })}
      </span>
      <span
        className={clsx(
          "log__item--level",
          level && `log__item--level_${level.toLowerCase()}`,
        )}
      >
        {level || RichLogLevel.Debug}
      </span>
      {group && <span className="log__item--group">Group: {group}</span>}
      {step && <span className="log__item--step">{step}</span>}

      {!!method && showDebugInfo && (
        <div className="log__item--method-line">
          <span className="log__item--method-label">Method:</span>
          <span
            className="log__item--method-value"
            dangerouslySetInnerHTML={{
              // eslint-disable-next-line @typescript-eslint/naming-convention
              __html: methodWithDiagnostics || method,
            }}
          />
        </div>
      )}

      {showAiHint && aiHintHtml !== undefined && (
        <div className="log__item--ai-hint-line">
          <span className="log__item--ai-hint-label">AI hint:</span>
          <span
            className="log__item--ai-hint-value"
            // eslint-disable-next-line @typescript-eslint/naming-convention
            dangerouslySetInnerHTML={{ __html: aiHintHtml }}
          />
        </div>
      )}

      {messageHtml && (
        <p
          className="log__item--message"
          // eslint-disable-next-line @typescript-eslint/naming-convention
          dangerouslySetInnerHTML={{ __html: messageHtml }}
        />
      )}
      <RichLogsIdStripRow searchText={searchText} strips={idStrips} />

      {!!shouldShowTranslatedItem && (
        <p className="log__item--translated-info">
          {translateInfoItemDecoded(translatableItem, t)}
        </p>
      )}
      {!!shouldShowTranslatedError && (
        <p className="log__item--translated-error">
          <span role="img" aria-label="Error">
            ⚠️
          </span>{" "}
          {translateInfoItemDecoded(error, t)}
        </p>
      )}

      {!!shouldShowErrorDetails && (
        <div className="log__item--error-details">
          <div
            className="log__item--error-details-content"
            dangerouslySetInnerHTML={{
              // eslint-disable-next-line @typescript-eslint/naming-convention
              __html: errorDetailsHtml || errorDetails,
            }}
          />
        </div>
      )}
    </div>
  );
};
